class BundleBuilder {
  constructor(formElement) {
    this.form = formElement;
    this.preselectedItems = formElement.querySelectorAll(
      'input[name="preselected-items"]',
    );

    this.state = {
      totalSelected: this.preselectedItems.length,
      isComplete: false,
      isScrolling: false,
    };

    this.config = {
      preselectedCount: parseInt(this.form.dataset.preselectedCount) || 0,
      totalRequired: parseInt(this.form.dataset.totalRequired) || 0,
    };

    this.elements = {
      sections: this.form.querySelectorAll(".cl-bundle-section"),
      selectedCount: document.querySelector(".cl-selected-count"),
      totalRequired: document.querySelector(".cl-total-required"),
      submitButton: this.form.querySelector(".js-bundle-submit"),
      submitTrigger: document.querySelector("[data-submit-bundle]"),
      overviewButton: document.querySelector(
        '[aria-controls="bundle-overview"]',
      ),
      selectedItemsGrid: document.querySelector("[data-selected-items-grid]"),
      emptyMessage: document.querySelector("[data-empty-message]"),
      progressBackground: document.querySelector(".bundle-progress-bg"),
      overlay: document.querySelector(".bundle-overlay"),
      bundleComparePrice: document.querySelector("#bundle-compare-price"),
      bundleSalePrice: document.querySelector("#bundle-sale-price"),
    };

    this.init();
  }

  init() {
    this.initializeSwipers();
    this.attachEventListeners();
    this.updateProgress();
    this.setupFormValidation();
  }

  initializeSwipers() {
    this.form.querySelectorAll(".cl-bundle-item-card").forEach((item) => {
      new Swiper(item, {
        slidesPerView: 1,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
      });
    });
  }

  attachEventListeners() {
    this.form.addEventListener("change", this.handleInputChange.bind(this));

    // Update overview panel toggling to include overlay
    this.elements.overviewButton?.addEventListener("click", () => {
      const isExpanded =
        this.elements.overviewButton.getAttribute("aria-expanded") === "true";
      this.elements.overviewButton.setAttribute("aria-expanded", !isExpanded);
      this.elements.overlay?.classList.toggle("active", !isExpanded);
      document.body.style.overflow = !isExpanded ? "hidden" : "";
    });

    // Prevent clicks inside overview from bubbling to overlay
    document
      .querySelector("#bundle-overview")
      ?.addEventListener("click", (event) => {
        event.stopPropagation();
      });

    // Update close overview when clicking overlay
    this.elements.overlay?.addEventListener("click", (event) => {
      if (event.target === this.elements.overlay) {
        this.elements.overviewButton.setAttribute("aria-expanded", "false");
        this.elements.overlay.classList.remove("active");
        document.body.style.overflow = "";
      }
    });

    // Add escape key handler
    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        this.elements.overviewButton?.getAttribute("aria-expanded") === "true"
      ) {
        this.elements.overviewButton.setAttribute("aria-expanded", "false");
        this.elements.overlay?.classList.remove("active");
        document.body.style.overflow = "";
      }
    });
  }

  revealIncompleteSections() {
    let shouldReveal = false;
    this.elements.sections.forEach((section) => {
      const required = parseInt(section.dataset.requiredItems, 10);
      const checked = section.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;

      // Start revealing sections once we find an incomplete one
      if (checked < required) {
        shouldReveal = true;
      }

      if (shouldReveal) {
        section.classList.remove("tw-hidden");
      }
    });
  }

  handleInputChange(event) {
    if (!event.target.matches('input[type="checkbox"]')) return;

    const checkbox = event.target;
    const section = checkbox.closest(".cl-bundle-section");
    const requiredItems = parseInt(section.dataset.requiredItems);

    if (checkbox.dataset.correspondingBundleReference) {
      this.updateBundleVariant(checkbox.dataset.correspondingBundleReference);
    }

    this.updateSectionState(section, requiredItems);
    this.revealIncompleteSections();
    this.updateProgress();
  }

  updateBundleVariant(variantId) {
    this.form.dataset.bundleVariantId = variantId;
    this.updateBundlePricing(variantId);
  }

  updateBundlePricing(variantId) {
    const allBundleVariants = JSON.parse(this.form.dataset.allBundleVariants);
    const bundleDetails = allBundleVariants.find(
      (bundle) => Number(bundle.id) === Number(variantId),
    );

    if (!bundleDetails) return;

    const { shopLocale, shopCurrency } = this.form.dataset;
    const formatter = new Intl.NumberFormat(shopLocale, {
      style: "currency",
      currency: shopCurrency,
    });

    const compareAtPrice = formatter.format(
      bundleDetails.compare_at_price / 100,
    );
    const salePrice = formatter.format(bundleDetails.price / 100);

    this.elements.bundleComparePrice.textContent = compareAtPrice;
    this.elements.bundleSalePrice.textContent = salePrice;
  }

  updateSectionState(section, requiredItems) {
    const checkedBoxes = section.querySelectorAll(
      'input[type="checkbox"]:checked',
    );
    const uncheckedBoxes = section.querySelectorAll(
      'input[type="checkbox"]:not(:checked)',
    );

    if (checkedBoxes.length >= requiredItems) {
      uncheckedBoxes.forEach((box) => (box.disabled = true));
      this.showNextSection(section);
    } else {
      uncheckedBoxes.forEach((box) => (box.disabled = false));
      section.classList.remove("tw-hidden");
    }
  }

  showNextSection(currentSection) {
    const nextSection = currentSection.nextElementSibling;
    if (nextSection && nextSection.classList.contains("cl-bundle-section")) {
      nextSection.classList.remove("tw-hidden");
      this.scrollToSection(nextSection);
    }
  }

  scrollToSection(section) {
    if (this.state.isScrolling) return;

    this.state.isScrolling = true;
    const headerOffset = window.innerWidth < 1024 ? 64 : 140;
    const offsetPosition =
      section.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    // Reset scrolling state after animation
    setTimeout(() => {
      this.state.isScrolling = false;
    }, 500);
  }

  findFirstIncompleteSection() {
    for (const section of this.elements.sections) {
      const required = parseInt(section.dataset.requiredItems, 10);
      const checked = section.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;

      if (checked < required) {
        return section;
      }
    }
    return null;
  }

  scrollToFirstIncompleteSection() {
    const incompleteSection = this.findFirstIncompleteSection();
    if (incompleteSection) {
      this.scrollToSection(incompleteSection);
    }
  }

  updateProgress() {
    // Start with preselected items
    let totalSelected = this.preselectedItems.length;

    // Add selected items from each section
    this.elements.sections.forEach((section) => {
      const checked = section.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;
      section.dataset.selectedItems = checked;
      totalSelected += checked;
    });

    // Save to state
    this.state.totalSelected = totalSelected;

    // Update counter text
    this.elements.selectedCount.textContent = totalSelected;
    this.elements.totalRequired.textContent = this.config.totalRequired;

    // Update completion state
    this.state.isComplete = totalSelected === this.config.totalRequired;
    this.updateCompletionUI();

    // Update aria-live region for accessibility
    const statusMessage = `${totalSelected} of ${this.config.totalRequired} items selected`;
    const bundleStatus = document.getElementById("bundle-status");
    if (bundleStatus) {
      bundleStatus.textContent = statusMessage;
    }

    // Update progress bar width
    if (this.elements.progressBackground) {
      const progressPercentage =
        (this.state.totalSelected / this.config.totalRequired) * 100;
      this.elements.progressBackground.style.width = `${Math.min(100, progressPercentage)}%`;
    }

    this.updateSelectedItemsOverview();
  }

  updateCompletionUI() {
    if (this.state.isComplete) {
      this.elements.submitTrigger.classList.remove("!tw-hidden");
    } else {
      this.elements.submitTrigger.classList.add("!tw-hidden");
    }
  }

  setupFormValidation() {
    // Store bound event handlers as class properties
    this.handleSubmitTrigger = () => this.elements.submitButton?.click();
    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    this.elements.submitTrigger?.addEventListener(
      "click",
      this.handleSubmitTrigger,
    );
    this.form.addEventListener("submit", this.handleFormSubmit);
  }

  async handleFormSubmit(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    try {
      this.setSubmitLoading(true);
      await this.addBundleToCart();
      window.location.assign("/cart");
    } catch (error) {
      this.setSubmitLoading(false);
      this.showError("Failed to add bundle to cart. Please try again.");
    }
  }

  setSubmitLoading(isLoading) {
    const submitText =
      this.elements.submitTrigger?.querySelector("[data-submit-text]");
    const loadingIndicator = this.elements.submitTrigger?.querySelector(
      "[data-loading-indicator]",
    );

    if (isLoading) {
      submitText.textContent = "Adding to Cart";
      loadingIndicator?.classList.remove("tw-hidden");
      this.elements.submitTrigger.disabled = true;
      this.elements.submitButton.disabled = true;
    } else {
      submitText.textContent = "Add to Cart";
      loadingIndicator?.classList.add("tw-hidden");
      this.elements.submitTrigger.disabled = false;
      this.elements.submitButton.disabled = false;
    }
  }

  async addBundleToCart() {
    const bundleVariantId = this.form.dataset.bundleVariantId;
    const selectedItems = this.getSelectedBundleComponents();

    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: parseInt(bundleVariantId),
            quantity: 1,
            properties: {
              _selected_custom_bundle_components: selectedItems,
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error("Cart addition failed");
    }

    return response.json();
  }

  getSelectedBundleComponents() {
    return Array.from(
      this.form.querySelectorAll('input[type="checkbox"]:checked'),
    ).map((checkbox) => `gid://shopify/ProductVariant/${checkbox.value}`);
  }

  showError(message) {
    // You might want to implement a better error UI than alert
    alert(message);
  }

  validateForm() {
    let isValid = true;
    this.elements.sections.forEach((section, index) => {
      const required = parseInt(section.dataset.requiredItems, 10);
      const checked = section.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;

      if (checked < required) {
        isValid = false;
        this.showValidationError(section, index + 1, required);
      }
    });

    return isValid;
  }

  showValidationError(section, stepNumber, required) {
    // Show native-like validation message
    const heading = section.querySelector("h2");
    heading.scrollIntoView({ behavior: "smooth", block: "center" });

    alert(`Please select ${required} item(s) in Step ${stepNumber}`);
  }

  updateSelectedItemsOverview() {
    if (!this.elements.selectedItemsGrid) return;

    // Clear existing items first
    const existingItems =
      this.elements.selectedItemsGrid.querySelectorAll(".selected-item");
    existingItems.forEach((item) => item.remove());

    const selectedItems = Array.from(
      this.form.querySelectorAll(
        'input[type="checkbox"]:checked:not([name="preselected-items"])',
      ),
    );

    // Check for empty state after clearing
    if (selectedItems.length === 0) {
      this.elements.emptyMessage?.classList.remove("tw-hidden");
      return;
    }

    this.elements.emptyMessage?.classList.add("tw-hidden");

    // Add selected items
    selectedItems.forEach((checkbox) => {
      const label = checkbox.nextElementSibling;
      if (!label) return;

      const image = label.querySelector("img");
      if (!image) return;

      const thumbnail = document.createElement("div");
      thumbnail.className =
        "selected-item tw-aspect-square tw-relative tw-rounded-md tw-overflow-hidden tw-border tw-border-gray-200 tw-shadow-sm tw-bg-gray-50";
      thumbnail.setAttribute("data-checkbox-id", checkbox.id);

      const thumbnailImage = document.createElement("img");
      thumbnailImage.src = image.src;
      thumbnailImage.alt = image.alt;
      thumbnailImage.className =
        "tw-w-full tw-h-full tw-object-cover tw-transition-all tw-duration-300";
      thumbnailImage.loading = "lazy";

      const removeButton = document.createElement("button");
      removeButton.type = "button";
      removeButton.className =
        "tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-w-7 tw-h-7 tw-rounded-full tw-bg-black tw-flex tw-items-center tw-justify-center tw-text-white hover:tw-scale-110 tw-transition-transform tw-duration-200 tw-shadow-sm tw-text-3xl tw-leading-[0] tw-pb-0.5";
      removeButton.textContent = "×";

      thumbnail.appendChild(thumbnailImage);
      thumbnail.appendChild(removeButton);

      // Add click handler to uncheck the corresponding checkbox
      removeButton.addEventListener("click", (event) => {
        event.stopPropagation();
        const targetCheckbox = document.getElementById(checkbox.id);
        if (targetCheckbox) {
          targetCheckbox.checked = false;
          targetCheckbox.dispatchEvent(new Event("change", { bubbles: true }));

          // Close the overview panel
          this.elements.overviewButton.setAttribute("aria-expanded", "false");
          this.elements.overlay?.classList.remove("active");
          document.body.style.overflow = "";

          // Scroll to the first incomplete section after the overview closes
          setTimeout(() => {
            this.scrollToFirstIncompleteSection();
          }, 400);
        }
      });

      this.elements.selectedItemsGrid.appendChild(thumbnail);
    });
  }

  resetFormState() {
    // Remove existing event listeners
    this.elements.submitTrigger?.removeEventListener(
      "click",
      this.handleSubmitTrigger,
    );
    this.form.removeEventListener("submit", this.handleFormSubmit);

    // Uncheck all non-preselected checkboxes
    this.form
      .querySelectorAll(
        'input[type="checkbox"]:not([name="preselected-items"])',
      )
      .forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.disabled = false;
      });

    // Reset all section states
    this.elements.sections.forEach((section) => {
      section.dataset.selectedItems = "0";
      if (!section.classList.contains("cl-bundle-section")) return;

      // Hide all sections except the first one
      if (!section.querySelector('input[name="preselected-items"]')) {
        section.classList[
          section === this.elements.sections[0] ? "remove" : "add"
        ]("tw-hidden");
      }
    });

    // Reattach event listeners
    this.setupFormValidation();

    // Reset submit button state
    this.elements.submitButton.disabled = false;

    // Reset UI elements and state
    this.state.totalSelected = this.preselectedItems.length;
    this.state.isComplete = false;

    this.elements.submitTrigger?.classList.add("!tw-hidden");
    this.elements.overviewButton?.setAttribute("aria-expanded", "false");
    this.elements.overlay?.classList.remove("active");
    document.body.style.overflow = "";

    // Reset loading state
    this.setSubmitLoading(false);

    // Reset progress and force a complete UI update
    this.updateProgress();
    this.updateCompletionUI();
  }
}

// Initialize bundle builder
document.addEventListener("DOMContentLoaded", () => {
  const formEl = document.querySelector("#cl-custom-bundles");
  if (formEl) {
    window.bundleBuilder = new BundleBuilder(formEl);
  }
});

// Handle browser navigation
window.addEventListener("pageshow", (event) => {
  if (
    event.persisted ||
    performance?.getEntriesByType("navigation")[0]?.type === "back_forward"
  ) {
    window.bundleBuilder?.resetFormState();
  }
});

window.addEventListener("popstate", () => {
  window.bundleBuilder?.resetFormState();
});
